import { useState } from 'react';
import type { FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Handshake, FileText, ScrollText } from 'lucide-react';
import { AppShell } from '@/components/layout/AppShell';
import { Card, CardHeader, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Field, Input, Select, Textarea } from '@/components/ui/Field';
import { useToast } from '@/components/ui/ToastContext';
import { submitPartnerApplication, REQUIRED_DOC_KEYS, OPTIONAL_DOC_KEYS, DOC_LABELS, DOC_NUMBER_FIELDS } from '@/lib/partnerApplications';
import type { PartnerDocFiles, PartnerDocKey } from '@/lib/partnerApplications';
import { DocUploadCard } from '@/components/partners/DocUploadCard';
import { rxkModuleIcon, rxkModuleTitle, rxkNavGroups } from '../../nav';

const EMPTY_APPLICATION_FORM = {
  business_name: '',
  business_type: 'pharmacy',
  contact_person: '',
  designation: '',
  email: '',
  phone: '',
  city: '',
  address: '',
  monthly_volume: '',
  why_partner: '',
  drug_license_no: '',
  tin_no: '',
  nid_no: '',
  bin_no: '',
  agreement_terms: '',
  agreement_start_date: '',
  agreement_end_date: '',
  advance_payment_pct: '50',
  first_order_advance_pct: '0',
};

// DOC_LABELS bakes "(optional)" into the label text for the public form,
// which has no separate required/optional badge — strip it here since this
// page already shows that via the Required/Optional Badge on each card.
function cleanLabel(label: string): string {
  return label.replace(' (optional)', '');
}

export default function AdminResellerNew() {
  const navigate = useNavigate();
  const { push } = useToast();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(EMPTY_APPLICATION_FORM);
  const [files, setFiles] = useState<PartnerDocFiles>({});

  const requiredDone = REQUIRED_DOC_KEYS.filter((k) => files[k]).length;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setBusy(true);
    const { error } = await submitPartnerApplication(
      {
        ...form,
        advance_payment_pct: Math.min(100, Math.max(0, Number(form.advance_payment_pct) || 0)),
        first_order_advance_pct: Math.min(100, Math.max(0, Number(form.first_order_advance_pct) || 0)),
      },
      files,
    );
    setBusy(false);
    if (error) {
      push('error', error);
      return;
    }
    push('success', 'Submitted for approval — it will appear in the directory once approved in ERP');
    navigate('/admin/resellers');
  };

  return (
    <AppShell moduleTitle={rxkModuleTitle} moduleIcon={rxkModuleIcon} navGroups={rxkNavGroups}>
      <form onSubmit={handleSubmit} className="space-y-5 pb-10">
        <div className="rounded-2xl bg-gradient-to-br from-primary-700 via-primary-800 to-primary-900 p-5 sm:p-6 text-white shadow-pop space-y-4">
          <div className="flex items-center gap-4">
            <button
              type="button"
              onClick={() => navigate('/admin/resellers')}
              className="w-9 h-9 flex-shrink-0 flex items-center justify-center rounded-lg bg-white/10 hover:bg-white/20 text-white cursor-pointer"
              aria-label="Back"
            >
              <ArrowLeft className="w-4 h-4" />
            </button>
            <div className="w-11 h-11 flex-shrink-0 rounded-xl bg-white/15 flex items-center justify-center">
              <Handshake className="w-5 h-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h1 className="text-[19px] font-bold leading-tight">Add Reseller</h1>
              <p className="text-[12px] text-white/70">Sent for approval in ERP — becomes active here once approved.</p>
            </div>
          </div>
          <div className="flex items-center justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => navigate('/admin/resellers')} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" loading={busy} className="!bg-none !bg-white !text-primary-800 hover:!bg-white/90 !shadow-none">
              Submit for Approval
            </Button>
          </div>
        </div>

        <Card>
          <CardHeader title="Business Information" icon={<Handshake className="w-4 h-4" />} />
          <CardBody className="space-y-3.5">
            <Field label="Business Name">
              <Input value={form.business_name} onChange={(e) => setForm((f) => ({ ...f, business_name: e.target.value }))} />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Type">
                <Select value={form.business_type} onChange={(e) => setForm((f) => ({ ...f, business_type: e.target.value }))}>
                  <option value="pharmacy">Pharmacy</option>
                  <option value="beauty">Beauty</option>
                  <option value="online">Online</option>
                  <option value="distributor">Distributor</option>
                </Select>
              </Field>
              <Field label="City">
                <Input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Contact Person">
                <Input value={form.contact_person} onChange={(e) => setForm((f) => ({ ...f, contact_person: e.target.value }))} />
              </Field>
              <Field label="Designation">
                <Input
                  value={form.designation}
                  onChange={(e) => setForm((f) => ({ ...f, designation: e.target.value }))}
                  placeholder="e.g. Owner, Manager, Pharmacist-in-charge"
                />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Phone">
                <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
              </Field>
              <Field label="Email">
                <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} />
              </Field>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Business Address">
                <Input value={form.address} onChange={(e) => setForm((f) => ({ ...f, address: e.target.value }))} />
              </Field>
              <Field label="Monthly Volume">
                <Input
                  list="monthly-volume-options"
                  value={form.monthly_volume}
                  onChange={(e) => setForm((f) => ({ ...f, monthly_volume: e.target.value }))}
                  placeholder="Pick a range or type your own"
                />
                <datalist id="monthly-volume-options">
                  <option value="Under 100 units/month" />
                  <option value="100–500 units/month" />
                  <option value="500–1,000 units/month" />
                  <option value="1,000+ units/month" />
                </datalist>
              </Field>
            </div>
            <Field label="Why Partner">
              <Textarea
                rows={3}
                maxLength={500}
                value={form.why_partner}
                onChange={(e) => setForm((f) => ({ ...f, why_partner: e.target.value }))}
                placeholder="Why do they want to partner with us?"
              />
            </Field>
          </CardBody>
        </Card>

        <Card>
          <CardHeader
            title="Documents"
            subtitle={`${requiredDone} of ${REQUIRED_DOC_KEYS.length} required documents added`}
            icon={<FileText className="w-4 h-4" />}
          />
          <CardBody className="space-y-5">
            {(() => {
              // 'agreement' is placed in the Agreement card below instead,
              // alongside the terms/dates it belongs with.
              const allKeys = [...REQUIRED_DOC_KEYS, ...OPTIONAL_DOC_KEYS].filter((k) => k !== 'agreement') as PartnerDocKey[];
              const numberedKeys = allKeys.filter((k) => DOC_NUMBER_FIELDS[k]);
              const plainKeys = allKeys.filter((k) => !DOC_NUMBER_FIELDS[k]);
              return (
                <>
                  {numberedKeys.map((key) => {
                    const required = (REQUIRED_DOC_KEYS as readonly string[]).includes(key);
                    const numberField = DOC_NUMBER_FIELDS[key]!;
                    return (
                      <div key={key} className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 items-start">
                        <Field label={numberField.label}>
                          <Input
                            value={form[numberField.key]}
                            onChange={(e) => setForm((f) => ({ ...f, [numberField.key]: e.target.value }))}
                          />
                        </Field>
                        <DocUploadCard
                          label={cleanLabel(DOC_LABELS[key])}
                          required={required}
                          file={files[key] ?? null}
                          onChange={(file) => setFiles((f) => ({ ...f, [key]: file }))}
                        />
                      </div>
                    );
                  })}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3.5">
                    {plainKeys.map((key) => {
                      const required = (REQUIRED_DOC_KEYS as readonly string[]).includes(key);
                      return (
                        <DocUploadCard
                          key={key}
                          label={cleanLabel(DOC_LABELS[key])}
                          required={required}
                          file={files[key] ?? null}
                          onChange={(file) => setFiles((f) => ({ ...f, [key]: file }))}
                        />
                      );
                    })}
                  </div>
                </>
              );
            })()}
          </CardBody>
        </Card>

        <Card>
          <CardHeader title="Agreement & Consignment Terms" subtitle="Optional — can also be added later" icon={<ScrollText className="w-4 h-4" />} />
          <CardBody className="space-y-3.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="First Order Advance % (consignment)">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.first_order_advance_pct}
                  onChange={(e) => setForm((f) => ({ ...f, first_order_advance_pct: e.target.value }))}
                />
                <p className="text-[11px] text-ink-faint mt-1">Required upfront on their very first order. 0 = ships fully on credit.</p>
              </Field>
              <Field label="Advance Payment % (repeat orders)">
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={form.advance_payment_pct}
                  onChange={(e) => setForm((f) => ({ ...f, advance_payment_pct: e.target.value }))}
                />
                <p className="text-[11px] text-ink-faint mt-1">Required upfront on every order after the first.</p>
              </Field>
            </div>
            <Field label="Terms & Conditions">
              <Textarea
                rows={5}
                value={form.agreement_terms}
                onChange={(e) => setForm((f) => ({ ...f, agreement_terms: e.target.value }))}
                placeholder="Write the terms agreed with this partner…"
              />
            </Field>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <Field label="Agreement Start Date">
                <Input type="date" value={form.agreement_start_date} onChange={(e) => setForm((f) => ({ ...f, agreement_start_date: e.target.value }))} />
              </Field>
              <Field label="Agreement End Date">
                <Input type="date" value={form.agreement_end_date} onChange={(e) => setForm((f) => ({ ...f, agreement_end_date: e.target.value }))} />
              </Field>
            </div>
            <div>
              <p className="text-[11px] font-semibold text-ink mb-1.5">Signed Agreement File</p>
              <DocUploadCard
                label={cleanLabel(DOC_LABELS.agreement)}
                required={false}
                file={files.agreement ?? null}
                onChange={(file) => setFiles((f) => ({ ...f, agreement: file }))}
              />
            </div>
          </CardBody>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" loading={busy}>
            Submit for Approval
          </Button>
        </div>
      </form>
    </AppShell>
  );
}
